/**
 * Prebook API
 *
 * Network-only functions for flight prebook operations.
 * NO Redux imports allowed in this file.
 *
 * Uses common flights API utilities which automatically add:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
import * as v from 'valibot';
import { FLIGHTS_ENDPOINTS } from '@/api/endpoints';
import { buildQueryParams, buildHeaders, buildUrl } from '@/api/utils';
import { logger } from '@/shared/utils/logger';
import {
  PrebookResponseSchema,
  type PrebookRequestParams,
  type PrebookResponse,
  type ApiErrorResponse,
  type PopupErrorDetails,
} from '@contracts';
import { extractPopupErrorDetails } from '@/features/flights/utils/apiErrorUtils';

// ============================================
// Error Types
// ============================================

/**
 * Structured error details from API response.
 * Used for displaying error popups with meaningful messages.
 * @deprecated Use PopupErrorDetails from @contracts instead
 */
export type PrebookErrorDetails = PopupErrorDetails;

// ============================================
// Error Classes
// ============================================

export class PrebookError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
    public readonly errorDetails?: PopupErrorDetails,
  ) {
    super(message);
    this.name = 'PrebookError';
  }
}

// ============================================
// API Functions
// ============================================

/**
 * Prebook flight
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function prebookFlight(params: PrebookRequestParams): Promise<PrebookResponse> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    itineraryId: params.itineraryId,
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
    cc: params.cabinClass ?? 'E',
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.PREBOOK, queryParams);
  const headers = buildHeaders();

  logger.warn('[PrebookClient] Making API request to:', url);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params.requestBody),
    });

    const responseData = (await response.json()) as unknown as ApiErrorResponse & unknown;

    // Check for BFF failure format: responseStatus "FAILED" + errorDetails (works for 4xx and 200 body)
    const errorDetails = extractPopupErrorDetails(responseData);
    if (errorDetails) {
      throw new PrebookError(
        errorDetails.errorMessage,
        responseData.errorDetails?.statusCode ?? response.status,
        responseData,
        errorDetails,
      );
    }

    if (!response.ok) {
      throw new PrebookError(
        `Prebook request failed with status ${response.status}`,
        response.status,
        responseData,
      );
    }

    try {
      const validatedResponse = v.parse(PrebookResponseSchema, responseData);
      return validatedResponse;
    } catch (validationError) {
      throw new PrebookError(
        'Invalid prebook response structure',
        response.status,
        validationError,
      );
    }
  } catch (error) {
    if (error instanceof PrebookError) {
      throw error;
    }

    const message =
      error instanceof Error
        ? `Failed to prebook flight: ${error.message}`
        : 'Failed to prebook flight: Unknown error';

    throw new PrebookError(message, undefined, error);
  }
}
